import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useGetReportSpecificFieldsQuery } from "../../../api/network/reportApiServices";
import { TModalsState } from "../../../api/store/commonSlice";
import svgIcons from "../../../assets/svg/svgIcons";
import { APP_CONFIG } from "../../../constants/constants";
import ModalSwitchChartType from "../components/modalSwitchChartType";
import { IWidgetProps } from "../components/overviewDragDropColumnItem";
import WidgetChartAreaGenerator from "../components/widgetChartAreaGenerator";
import { useOverviewContext } from "../context/overview.context";
import { useGroupsAndAssetsUtils } from "../hooks/overviewScreenUtils";
import { EWidgetChartTypes } from "../types/overview.types";

const chartColors = [
  "#92CEA3", // green
  "#FF5959", // red
  "#FFD059", // yellow
  "#CB904D", // orange
];
const titleKeyColorMapping = [
  { titleKey: "driving", color: chartColors[0] }, // green
  { titleKey: "ignition_off", color: chartColors[1] }, // red
  { titleKey: "idle", color: chartColors[2] }, // yellow
];

const reportId = 75;
const defaultChartType = EWidgetChartTypes.PIE;

const graphXAxisDataKey = "title";
const graphYAxisDataKey = "value";

const WidgetAssetStatusReport: FC<IWidgetProps> = ({ widgetType }) => {
  const { t: tOverview } = useTranslation("translation", {
    keyPrefix: "overviewScreen",
  });
  const { t } = useTranslation("translation", {
    keyPrefix: "overviewScreen.widgets.enum_5",
  });

  const modalsState = useSelector(
    (state: { commonReducer: { modals: TModalsState } }) =>
      state.commonReducer.modals
  );
  const [selectedChartType, setSelectedChartType] = useState(defaultChartType);

  const {
    allTimeFilters,
    timeFilter,
    allGroupsSelected,
    selectedGroups,
    selectedDataType,
    setSelectedDataType,
    allAssetsSelected,
    selectedAssets,
    setAllAssetsSelected,
    setSelectedAssets,
  } = useOverviewContext();

  const {
    data: dataSpecificReport,
    isFetching: isFetchingSpecificReport,
    isUninitialized: isUninitializedSpecificReport,
  } = useGetReportSpecificFieldsQuery(
    {
      // type: REPORT_TYPES.CUSTOM,
      report_id: reportId,
    },
    { refetchOnMountOrArgChange: true }
  );

  // const { data: dataReport, isFetching } = useRunReportQuery(
  //   {
  //     id: reportId.toString(),
  //     // fields: '443,444,447'.split(','),
  //     // ordering: '447',

  //     page: 1,
  //     page_size: 10,

  //     // filters
  //     device_groups:
  //       allGroupsSelected === false && !!selectedGroups
  //         ? selectedGroups
  //         : undefined,
  //     start_time: !!allTimeFilters.find((filter) => filter.slug === timeFilter)
  //       ?.timeRange[0]
  //       ? jsTimeToDBTime(
  //           allTimeFilters
  //             .find((filter) => filter.slug === timeFilter)
  //             ?.timeRange[0]?.getTime() ?? 0
  //         )
  //       : undefined,
  //     end_time: !!allTimeFilters.find((filter) => filter.slug === timeFilter)
  //       ?.timeRange[1]
  //       ? jsTimeToDBTime(
  //           allTimeFilters
  //             .find((filter) => filter.slug === timeFilter)
  //             ?.timeRange[1]?.getTime() ?? 0
  //         )
  //       : undefined,
  //     ds_time_range: allTimeFilters.find((filter) => filter.slug === timeFilter)
  //       ?.value
  //   },
  //   {
  //     skip: isUninitializedSpecificReport || isFetchingSpecificReport,
  //     refetchOnMountOrArgChange: true
  //   }
  // );

  const {
    reportData: dataReport,
    isFetching,
    isError,
    reportError,
  } = useGroupsAndAssetsUtils(selectedDataType, reportId, selectedAssets);

  const [computedChartData, setComputedChartData] = useState<any[]>([]);
  useEffect(() => {
    if (
      !!dataReport &&
      !!dataReport?.results &&
      !!dataSpecificReport?.report_fields &&
      !!dataReport?.results?.[0]
    ) {
      setComputedChartData(
        Object.keys(dataReport?.results?.[0]).map((columnKeys) => ({
          [graphXAxisDataKey]: dataSpecificReport?.report_fields?.find(
            (field) => field.field.id === parseInt(columnKeys)
          )?.field?.name,
          [graphYAxisDataKey]: dataReport?.results?.[0][columnKeys],
        }))
      );
    }
  }, [dataReport, dataSpecificReport]);

  return (
    <div className="mt-2">
      {APP_CONFIG.DEBUG.DISPLAY_VERBOSE && (
        <pre className="absolute text-[8px] text-field-error-dark -mt-2">
          reportId: {reportId}
        </pre>
      )}

      {isFetching ? (
        <div className="h-48 relative">
          <img
            src={svgIcons.LoadingAnimation}
            alt="laoding"
            className="absolute size-8 top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
          />
        </div>
      ) : !!dataReport &&
        !!dataReport?.results?.length &&
        dataReport?.results?.length > 0 ? (
        <>
          <WidgetChartAreaGenerator
            barChartMargin={
              selectedChartType === EWidgetChartTypes.HORIZONTAL_BAR_GRAPH
                ? { top: 0, right: 10, bottom: 0, left: -10 }
                : { top: 0, right: 0, left: -35, bottom: 0 }
            }
            cellItemsForSectionColor={
              !!dataReport &&
              !!dataReport?.results &&
              !!dataSpecificReport?.report_fields &&
              !!dataReport?.results?.[0]
                ? Object.keys(dataReport?.results?.[0])
                : []
            }
            // chartColors={chartColors}
            chartColors={
              // prepare colors based on titleKeyColorMapping
              !!dataReport &&
              !!dataReport?.results &&
              !!dataSpecificReport?.report_fields
                ? computedChartData
                    .filter((item) => item[graphXAxisDataKey] !== "total_count")
                    .sort((a, b) => b.value - a.value) // sort in descending order
                    // use titleKeyColorMapping to get color based on titleKey
                    .map(
                      (item) =>
                        titleKeyColorMapping.find(
                          (mapping) =>
                            mapping.titleKey === item[graphXAxisDataKey]
                        )?.color || "#000000"
                    )
                : []
            }
            chartHighlightLabel={"Total Assets"}
            chartHighlightValue={
              !!dataReport && !!dataReport?.results
                ? computedChartData?.find(
                    (item) => item[graphXAxisDataKey] === "total_count"
                  )?.value
                : 0
            }
            chartType={selectedChartType}
            chartWrapperHeightClass={
              selectedChartType === EWidgetChartTypes.HORIZONTAL_BAR_GRAPH
                ? "pt-[100%]"
                : "pt-[45%]"
            }
            computedChartData={
              !!dataReport &&
              !!dataReport?.results &&
              !!dataSpecificReport?.report_fields
                ? computedChartData
                    .filter((item) => item[graphXAxisDataKey] !== "total_count")
                    .sort((a, b) => b.value - a.value) // sort in descending order
                : []
            }
            legendHeading={t("fleet_wise_breakup")}
            legendLabelFormatter={(value: any) => t(`${value}`)}
            legendVerticalAlignClass="justify-center"
            t={t}
            widgetType={widgetType}
            xAxisTickLabelWidth={
              selectedChartType === EWidgetChartTypes.HORIZONTAL_BAR_GRAPH
                ? 40
                : 80
            }
          />

          {/* Legend */}
          {/* <div className="flex-1 flex items-center">
              <div className="w-full flex flex-col items-start gap-1 pr-2">
                <h4 className="font-semibold text-sm mb-1">{t('fleet_wise_breakup')}</h4>
                {
                  !!dataReport && !!dataReport?.results && !!dataSpecificReport?.report_fields
                    ? computedChartData
                      .filter((item) => item[graphXAxisDataKey] !== 'total_count')
                      .map((entry, index) => (
                        <div key={index} className="w-full flex items-center justify-between gap-2 mb-1 text-xs">
                          <div className="flex items-center">
                            <div className="shrink-0 size-3 rounded-sm mr-2" style={{ backgroundColor: chartColors[index % chartColors.length] }}></div>
                            <span className="flex-grow break-words">{t(`${entry.title}`)}</span>
                          </div>
                          <span className="ml-2 font-bold text-heading-gray666">{entry.value}</span>
                        </div>
                      ))
                    : []
                }
              </div>
            </div> */}

          {/* Pie Chart */}
          {/* <div className="flex-1 pt-[40%] pb-20 relative">
              <SwitchChartButton onClick={() => {
                dispatch(setModalsData({ ...modalsState, switchChartTypeForWidget: widgetType, showSwitchChartType: true }))
              }} />
              <ResponsiveContainer className={'absolute top-0 left-0 w-full h-full'}>
                <PieChart width={pieSize} height={pieSize} onMouseEnter={undefined}>
                  <Pie
                    data={
                      !!dataReport && !!dataReport?.results && !!dataSpecificReport?.report_fields
                        ? computedChartData
                          .filter((item) => item[graphXAxisDataKey] !== 'total_count')
                        : []
                    }
                    cx={"50%"}
                    cy={"45%"}
                    // innerRadius={"75%"} // {pieSize - 35}
                    outerRadius={"95%"}// {pieSize - 20}
                    fill="#8884d8"
                    // paddingAngle={5}
                    dataKey="value"
                  >
                    {
                      !!dataReport && !!dataReport?.results && !!dataSpecificReport?.report_fields && !!dataReport?.results?.[0] &&
                      Object.keys(dataReport?.results?.[0])
                        .map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={chartColors[index % chartColors.length]} />
                        ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
              <p className="absolute bottom-0 w-full flex flex-col text-xs leading-4 text-center">
                <span>Total Assets</span>
                <span className="font-semibold">
                  {
                    !!dataReport && !!dataReport?.results
                      ? computedChartData?.find((item) => item[graphXAxisDataKey] === 'total_count')?.value
                      : 0
                  }
                </span>
              </p>
            </div> */}
        </>
      ) : (
        <p className="p-6 text-center text-sm text-heading-gray888">
          {tOverview("no_widget_data")}
        </p>
      )}
      {modalsState.showSwitchChartType &&
        modalsState.switchChartTypeForWidget === widgetType && (
          <ModalSwitchChartType
            selectedChartType={selectedChartType}
            onChartTypeChange={(newChartType) => {
              setSelectedChartType(newChartType);
            }}
          />
        )}
    </div>
  );
};
export default WidgetAssetStatusReport;
